import { type UseQueryResult, useQueries, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { type AdminApproval, type Role } from '@column/api';

import { ROUTE } from '~/app/routes';
import { getOverrideIds } from '~/lib/approvals/roleDiff';
import { type AccountOwners } from '~/lib/approvals/types';
import { findRole } from '~/lib/approvals/utils';
import { BankAccountRepository } from '~/repositories/BankAccountRepository';
import { EntityRepository } from '~/repositories/EntityRepository';
import { useSessionStore } from '~/stores/Session';
import { EntityEntityWithDetails } from '~/typings/API';

const BATCH_SIZE = 100;
const STALE_TIME_MS = 60_000;

export interface OverrideTarget {
  name: string;
  path?: string;
}

const entityName = (entity: EntityEntityWithDetails) =>
  entity.businessDetails?.businessName ??
  (entity.personDetails ? `${entity.personDetails.firstName} ${entity.personDetails.lastName}` : entity.id);

const chunk = (ids: string[]) =>
  Array.from({ length: Math.ceil(ids.length / BATCH_SIZE) }, (_, i) => ids.slice(i * BATCH_SIZE, (i + 1) * BATCH_SIZE));

const combineEntityResults = (results: UseQueryResult<EntityEntityWithDetails>[]) => ({
  entities: results.map((result) => result.data).filter((entity): entity is EntityEntityWithDetails => !!entity?.id),
  isLoading: results.some((result) => result.isLoading),
});

const collectOverrideIds = (approvals: AdminApproval[], roles: KeysCamelCaseDeep<Role>[]) => {
  const entities = new Set<string>();
  const bankAccounts = new Set<string>();
  const add = (entityOverrides?: unknown[] | null, bankAccountOverrides?: unknown[] | null) => {
    getOverrideIds(entityOverrides, 'entityId').forEach((id) => entities.add(id));
    getOverrideIds(bankAccountOverrides, 'bankAccountId').forEach((id) => bankAccounts.add(id));
  };
  approvals.forEach((approval) => {
    const request = approval.request;
    if (request.user_role_assign) {
      [findRole(roles, request.user_role_assign.previous), findRole(roles, request.user_role_assign)].forEach((role) =>
        add(role?.entityLevelOverrides, role?.bankAccountLevelOverrides)
      );
    }
    if (request.role_update) {
      const { update, previous } = request.role_update;
      [previous, update].forEach((fields) => add(fields.entity_level_overrides, fields.bank_account_level_overrides));
    }
  });
  return { entityIds: Array.from(entities).sort(), bankAccountIds: Array.from(bankAccounts).sort() };
};

export const useApprovalTargets = (approvals: AdminApproval[], roles: KeysCamelCaseDeep<Role>[], enabled: boolean) => {
  const { currentPlatform } = useSessionStore();
  const platformId = currentPlatform?.id;
  const isEnabled = enabled && !!platformId;
  const modes = useMemo<('sandbox' | 'live')[]>(
    () => (currentPlatform?.isLiveEnabled ? ['sandbox', 'live'] : ['sandbox']),
    [currentPlatform?.isLiveEnabled]
  );
  const { entityIds, bankAccountIds } = useMemo(() => collectOverrideIds(approvals, roles), [approvals, roles]);

  const bankAccountQuery = useQuery({
    queryKey: ['bankAccounts', 'byIds', platformId, modes, bankAccountIds],
    queryFn: async () => {
      const results = await Promise.all(
        modes.flatMap((mode) => chunk(bankAccountIds).map((ids) => BankAccountRepository.getByIds({ ids, mode })))
      );
      return results.flatMap((result) => result.bankAccounts);
    },
    enabled: isEnabled && bankAccountIds.length > 0,
    staleTime: STALE_TIME_MS,
  });

  const entityQueries = useQueries({
    queries: modes.flatMap((mode) =>
      entityIds.map((id) => ({
        queryKey: ['entities', 'get', platformId, mode, id],
        queryFn: async () => EntityRepository.getWithMode(id, mode),
        enabled: isEnabled,
        retry: false,
        staleTime: STALE_TIME_MS,
      }))
    ),
    combine: combineEntityResults,
  });

  const targets = useMemo(() => {
    const map = new Map<string, OverrideTarget>();
    entityQueries.entities.forEach((entity) =>
      map.set(entity.id, {
        name: entityName(entity),
        path: `${ROUTE.ENTITIES}/edit/${String(entity.type ?? 'PERSON').toLowerCase()}/${entity.id}`,
      })
    );
    (bankAccountQuery.data ?? []).forEach((account) =>
      map.set(account.id, {
        name: account.displayName ?? account.description ?? account.id,
        path: `${ROUTE.BANK_ACCOUNTS}/edit/${account.id}`,
      })
    );
    return map;
  }, [bankAccountQuery.data, entityQueries.entities]);

  const accountOwners = useMemo<AccountOwners>(
    () => new Map((bankAccountQuery.data ?? []).map((account) => [account.id, account.owners ?? []] as const)),
    [bankAccountQuery.data]
  );

  return { targets, accountOwners, isLoading: bankAccountQuery.isLoading || entityQueries.isLoading };
};
